import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useBreadcrumbs } from "@/app-shell";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import { formatDateRange } from "@/core/utils/date";
import { IcmLineItemSidebar } from "@/icm/common";
import { useGetIcmStatementDetail } from "@/icm/query";
import { TechnicianStatementPage } from "@/icm/technician-payroll";
import {
  getPayBreakdownPath,
  getPayBreakdownStatementPath,
} from "@/payroll/pay-breakdown/routes";
import { isTech } from "@/user/utils";
import { IcmStatementRuleTable } from "./statement-rule-table";
import { IcmStatementSummaryCard } from "./statement-summary-card";
import { groupLineItemsByRule } from "./utils";

const AdminIcmStatementPage = () => {
  const { statementId, periodStart } = useParams<{
    statementId: string;
    periodStart: string;
  }>();
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const {
    data: statement,
    isError,
    isFetching,
    refetch,
  } = useGetIcmStatementDetail(businessId, statementId);

  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(
    null,
  );

  const allItems = statement?.line_items ?? [];
  const jobItems = allItems.filter((item) => item.job_id != null);
  const byRule = useMemo(() => groupLineItemsByRule(jobItems), [jobItems]);

  const selectedLineItem = useMemo(
    () =>
      selectedLineItemId
        ? ((statement?.line_items ?? []).find(
            (item) => item.id === selectedLineItemId,
          ) ?? null)
        : null,
    [selectedLineItemId, statement?.line_items],
  );

  const statementPeriodStart = periodStart ?? statement?.period_start;

  useBreadcrumbs([
    {
      label: "Payroll",
      href: statementPeriodStart
        ? getPayBreakdownPath(statementPeriodStart)
        : undefined,
    },
    { label: statement?.technician_name ?? "Statement" },
  ]);

  useEffect(() => {
    if (!periodStart && statementId && statement?.period_start) {
      navigate(
        getPayBreakdownStatementPath(statement.period_start, statementId),
        {
          replace: true,
        },
      );
    }
  }, [navigate, periodStart, statement?.period_start, statementId]);

  return (
    <SimpleSidebarLayout
      open={!!selectedLineItem}
      onOpenChange={(open) => !open && setSelectedLineItemId(null)}
    >
      <SimpleSidebarContent>
        <PageContainer className="overflow-x-hidden">
          <PageHeader
            title={
              statement ? (
                (statement.technician_name ?? "Statement")
              ) : isError ? (
                "Statement"
              ) : (
                <>
                  <span
                    aria-hidden
                    className="inline-block h-7 w-56 animate-pulse rounded-md bg-neutral align-middle"
                  />
                  <span className="sr-only">Loading statement</span>
                </>
              )
            }
            description={
              statement
                ? [
                    selectedBusiness?.name,
                    formatDateRange(
                      statement.period_start,
                      statement.period_end,
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : undefined
            }
          />
          {isError && !statement && (
            <Stack align="start" gap={3} role="alert">
              <Text tone="muted">
                Couldn't load this statement. Please try again.
              </Text>
              <Button
                variant="outline"
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </Stack>
          )}
          {statement && (
            <>
              <IcmStatementSummaryCard
                statement={statement}
                isSidebarOpen={!!selectedLineItem}
              />
              <Stack className="w-full" align="start" gap={8}>
                {Object.entries(byRule).map(([ruleName, items]) => (
                  <IcmStatementRuleTable
                    key={ruleName}
                    ruleName={ruleName}
                    items={items}
                    selectedLineItemId={selectedLineItem?.id ?? null}
                    onSelectLineItem={(item) => setSelectedLineItemId(item.id)}
                  />
                ))}
              </Stack>
            </>
          )}
        </PageContainer>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {selectedLineItem && (
          <IcmLineItemSidebar
            lineItem={selectedLineItem}
            onClose={() => setSelectedLineItemId(null)}
            businessId={businessId}
            statementId={statementId}
          />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};

const IcmStatementPage = () => {
  const { user } = useAuth();

  if (isTech(user?.role)) {
    return <TechnicianStatementPage />;
  }

  return <AdminIcmStatementPage />;
};

export default IcmStatementPage;
